export const CATEGORY_RENDER_LIMIT = 50;

export type CategoryOption = {
  gcid: string;
  category: string;
};

/**
 * Lista que o campo de categoria mostra: no maximo `limit` itens.
 *
 * Em ordem alfabetica e com busca que ignora acento. Sem isso, as ~3.900
 * categorias vinham na ordem do banco e o campo mostrava so as 50 primeiras:
 * parecia que a categoria nao existia (relato do Thiago em 23/09/2026, perfil
 * GUERRA GUNS - "Clube de tiro" esta no banco, mas fora das 50 exibidas).
 */
export const getVisibleCategories = (
  categories: CategoryOption[],
  query: string,
  selectedIds: string[] = [],
  selectedNames: string[] = [],
  limit = CATEGORY_RENDER_LIMIT
) => {
  const normalizedQuery = normalize(query);
  const ordered = [...categories].sort((a, b) =>
    a.category.localeCompare(b.category, "pt-BR")
  );

  if (normalizedQuery) {
    return ordered
      .filter((cat) => normalize(cat.category).includes(normalizedQuery))
      .slice(0, limit);
  }

  const selected = ordered.filter(
    (cat) =>
      selectedIds.includes(cat.gcid) || selectedNames.includes(cat.category)
  );
  const selectedKeys = new Set(selected.map((cat) => cat.gcid));
  const remaining = ordered.filter((cat) => !selectedKeys.has(cat.gcid));

  return [...selected, ...remaining].slice(0, limit);
};

type CategoryFields = {
  category?: string;
  categoryId?: string;
  secondaryCategories?: string[];
  secondaryCategoryIds?: string[];
};

/**
 * Aplica no formulario uma categoria sugerida pela IA.
 *
 * A categoria principal que o perfil ja tem nunca e trocada por sugestao: a
 * sugestao marcada como "principal" entra como adicional. So vira principal
 * quando o perfil ainda nao tem nenhuma.
 */
export const applyCategorySuggestion = <T extends CategoryFields>(
  profile: T,
  suggestion: CategoryOption
): T => {
  if (!profile.categoryId) {
    return {
      ...profile,
      category: suggestion.category,
      categoryId: suggestion.gcid,
    };
  }

  if (profile.categoryId === suggestion.gcid) {
    return profile;
  }

  return {
    ...profile,
    secondaryCategories: Array.from(
      new Set([...(profile.secondaryCategories || []), suggestion.category])
    ).slice(0, 8),
    secondaryCategoryIds: Array.from(
      new Set([...(profile.secondaryCategoryIds || []), suggestion.gcid])
    ).slice(0, 8),
  };
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

